import { useState, useRef, useEffect } from 'react';
import { ReactionEmoji } from '../../hooks/useReactions';

interface ReactionPickerProps {
  onSelectReaction: (emoji: ReactionEmoji) => void;
}

const REACTION_OPTIONS: { emoji: ReactionEmoji; label: string }[] = [
  { emoji: '👍', label: 'Thumbs up' },
  { emoji: '👎', label: 'Thumbs down' },
  { emoji: '❤️', label: 'Heart' },
  { emoji: '👏', label: 'Clap' },
  { emoji: '😂', label: 'Laugh' },
  { emoji: '😮', label: 'Surprised' },
];

export function ReactionPicker({ onSelectReaction }: ReactionPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectReaction = (emoji: ReactionEmoji) => {
    onSelectReaction(emoji);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={pickerRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-lg transition-colors ${
          isOpen
            ? 'bg-aalaw-red text-white'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        title="Send reaction"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {/* Picker Popover */}
      {isOpen && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 p-2 z-50">
          <div className="flex gap-1">
            {REACTION_OPTIONS.map(({ emoji, label }) => (
              <button
                key={emoji}
                onClick={() => handleSelectReaction(emoji)}
                className="p-2 text-2xl hover:bg-gray-100 rounded-lg transition-colors"
                title={label}
              >
                {emoji}
              </button>
            ))}
          </div>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
            <div className="w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45"></div>
          </div>
        </div>
      )}
    </div>
  );
}
